
import { getOneSignalConfig } from './oneSignalConfig';

class NotificationService {
  private initialized = false;
  private initializationPromise: Promise<boolean> | null = null;

  async init(): Promise<boolean> {
    // Se già inizializzato, restituisci immediatamente
    if (this.initialized) {
      return true;
    }

    // Se inizializzazione in corso, attendi
    if (this.initializationPromise) {
      return await this.initializationPromise;
    }

    // Avvia nuova inizializzazione
    this.initializationPromise = this.performInitialization();
    return await this.initializationPromise;
  }

  private async performInitialization(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        console.warn('[NotificationService] Push notifications not supported');
        return false;
      }

      // Import dinamico OneSignal per evitare blocchi
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      
      // Inizializzazione con timeout
      await Promise.race([
        OneSignal.init(getOneSignalConfig()),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('OneSignal init timeout')), 8000)
        )
      ]);

      this.initialized = true;
      console.log('[NotificationService] Initialized successfully');
      return true;
    } catch (error) {
      console.error('[NotificationService] Initialization failed:', error);
      return false;
    }
  }

  isSupported(): boolean {
    return typeof window !== 'undefined' && 
           'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }

  async requestPermission(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        console.warn('[NotificationService] Not supported, using browser API');
        return await this.requestBrowserPermission();
      }

      // Tentativo con OneSignal (non-bloccante)
      try {
        const initialized = await Promise.race([
          this.init(),
          new Promise<boolean>((resolve) => 
            setTimeout(() => resolve(false), 3000)
          )
        ]);

        if (initialized) {
          const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
          await OneSignal.registerForPushNotifications();
          return true;
        }
      } catch (oneSignalError) {
        console.warn('[NotificationService] OneSignal permission failed:', oneSignalError);
      }

      // Fallback al browser nativo
      return await this.requestBrowserPermission();
    } catch (error) {
      console.error('[NotificationService] Permission request failed:', error);
      return false;
    }
  }

  private async requestBrowserPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('[NotificationService] Browser permission failed:', error);
      return false;
    }
  }

  async isPushNotificationsEnabled(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        return Notification.permission === 'granted';
      }

      const initialized = await this.init();
      if (!initialized) {
        return Notification.permission === 'granted';
      }

      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      return await OneSignal.isPushNotificationsEnabled();
    } catch (error) {
      console.warn('[NotificationService] Enable check failed:', error);
      return Notification.permission === 'granted';
    }
  }

  async setPriceAlertTags(userId: string, metal: string, targetPrice: number, condition: string): Promise<void> {
    try {
      const initialized = await this.init();
      if (!initialized) {
        console.warn('[NotificationService] Not initialized, skipping tags');
        return;
      }

      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      
      await OneSignal.sendTags({
        user_id: userId,
        price_alerts: 'enabled',
        [`${metal}_alert`]: 'true',
        [`${metal}_target_${condition}`]: targetPrice.toString()
      });

      console.log('[NotificationService] Price alert tags set successfully');
    } catch (error) {
      console.warn('[NotificationService] Failed to set tags:', error);
      // Non propagare l'errore - l'avviso può esistere senza tag
    }
  }

  async sendNotification(title: string, message: string, url?: string): Promise<void> {
    try {
      // Fallback alle notifiche browser se OneSignal non disponibile
      if (!this.initialized || !this.isSupported()) {
        this.sendBrowserNotification(title, message, url);
        return;
      }

      console.log('[NotificationService] Notification sent:', { title, message });
    } catch (error) {
      console.warn('[NotificationService] Notification failed:', error);
      this.sendBrowserNotification(title, message, url);
    }
  }

  private sendBrowserNotification(title: string, message: string, url?: string): void {
    try {
      if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico'
        });

        if (url) {
          notification.onclick = () => {
            window.open(url, '_blank');
            notification.close();
          };
        }
      }
    } catch (error) {
      console.warn('[NotificationService] Browser notification failed:', error);
    }
  }

  // Metodi aggiuntivi per compatibilità
  async getUserId(): Promise<string | null> {
    try {
      if (!this.initialized) return null;
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      return await OneSignal.getUserId();
    } catch {
      return null;
    }
  }

  async setExternalUserId(userId: string): Promise<void> {
    try {
      if (!this.initialized) return;
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      await OneSignal.setExternalUserId(userId);
    } catch (error) {
      console.warn('[NotificationService] Set external user ID failed:', error);
    }
  }

  async removeExternalUserId(): Promise<void> {
    try {
      if (!this.initialized) return;
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      await OneSignal.removeExternalUserId();
    } catch (error) {
      console.warn('[NotificationService] Remove external user ID failed:', error);
    }
  }

  async getTags(): Promise<{ [key: string]: string }> {
    try {
      if (!this.initialized) return {};
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      return await OneSignal.getTags();
    } catch {
      return {};
    }
  }

  async sendTag(key: string, value: string): Promise<void> {
    try {
      if (!this.initialized) return;
      const OneSignal = await import('./oneSignalWrapper.js').then(module => module.default);
      await OneSignal.sendTag(key, value);
    } catch (error) {
      console.warn('[NotificationService] Send tag failed:', error);
    }
  }
}

export const notificationService = new NotificationService();
